import { AuctionState, BusType, DirectSaleState, type SaleAssetBusEntity, SeoMetaTemplate } from '~/apiClient';
import { isAuction, type PlatformSaleItemEntity } from '~/types/sale.type';

type UseSdpSeoMetaReturn = {
    suspense: () => Promise<unknown>;
};

export const useSdpSeoMeta = (sale: Ref<PlatformSaleItemEntity>): UseSdpSeoMetaReturn => {
    // COMPOSABLES
    const { tt, formatUnits } = useTypedI18n();
    const { formatFirstRegistrationYear } = useAssetRegistration();
    const image = useImage();

    // DATA
    const mainAsset = computed<SaleAssetBusEntity>(() => sale.value.mainAsset);
    const type = computed<string>(() => {
        const busType = mainAsset.value.type;

        return busType && busType !== BusType.Other ? tt(`asset.busType.${busType}`) : '';
    });
    const values = computed(() => ({
        type: type.value,
        brand: mainAsset.value.brand.brandValue,
        model: mainAsset.value.model.modelValue,
        year: formatFirstRegistrationYear(mainAsset.value),
        km: formatUnits.km(mainAsset.value.km),
    }));
    const { title, description, suspense } = useSeoMetaTexts({
        template: SeoMetaTemplate.SaleDetail,
        sellingMethod: () => sale.value.sellingMethod,
        values,
    });
    const isAvailable = computed<boolean>(() =>
        isAuction(sale.value) ? sale.value.state === AuctionState.Running : sale.value.state === DirectSaleState.Open
    );
    const cover = computed<string | undefined>(() => mainAsset.value.images.cover[0]);

    // SEO
    useSeoMeta({
        title: () => title.value,
        ogTitle: () => withSiteName(title.value),
        description: () => description.value,
        ogDescription: () => description.value,
        ...setIf(!!cover.value, () => ({
            ogImage: {
                url: image(cover.value!, { width: 1280, height: 960 }),
                width: '1280px',
                height: '960px',
                alt: `Fleequid ${mainAsset.value.brand.brandValue} ${mainAsset.value.model.modelValue}`,
            },
        })),
    });

    // og:type product and the product:* namespace have no flat counterpart in useSeoMeta
    useHead(() => ({
        meta: [
            { property: 'og:type', content: 'product' },
            { property: 'product:availability', content: isAvailable.value ? 'in stock' : 'out of stock' },
            { property: 'product:condition', content: 'used' },
        ],
    }));

    return { suspense };
};
